import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import filterDuck from 'components/filter/filter-options/duck'
import Loader from 'components/loader'
import useSearch from 'components/shared/useSWR/useSearch'
import sortDuck from 'components/sort/sort-options/duck'
import {
  SEGMENT_STORAGE_KEY_SEARCH_ID,
  SEGMENT_STORAGE_KEY_SEARCH_IDX,
} from 'lib/segment/const'
import ValidSearchContent from './has-result'
import EmptySearchResult from './no-result'
import SearchRequestError from './request-error'
import styles from './style.scss'
import { checkHasFilterQuickSize, getSearchResultParams } from './utils'

const SearchContent = ({
  filter,
  filterableCategories,
  query,
  results: initialData,
  sort,
  sortAndFilterData,
  track,
  onChangeURL,
  sortBy,
  updateSort,
}) => {
  const isSearching = useRef(true)
  const [isFetchData, setIsFetchData] = useState(false)
  const [page, setPage] = useState(0)
  const [products, setProducts] = useState(initialData?.products || [])
  const [totalHits, setTotalHits] = useState(initialData?.totalHits)
  const [quickSizes, setQuickSize] = useState(initialData?.quick_sizes)
  const [sortData, setSortData] = useState(sortBy || 'popular')
  const [changeSort, setchangeSort] = useState(false)

  const fetchOptions = getSearchResultParams(filter, page, query, sortData)

  const {
    data: results,
    error,
    retry,
    isValidating,
  } = useSearch(fetchOptions, initialData)

  const invokeTrack = useCallback(
    (newResults) => {
      const {
        products: resProducts,
        totalHits: resTotal,
        queryID: queryId,
        index: query_idx,
      } = newResults

      sessionStorage.setItem(SEGMENT_STORAGE_KEY_SEARCH_ID, queryId)
      sessionStorage.setItem(SEGMENT_STORAGE_KEY_SEARCH_IDX, query_idx)

      track({
        hits: resProducts,
        query,
        page: page + 1,
        total: resTotal,
        query_id: queryId,
        query_idx,
      })
    },
    [query, track, page],
  )

  const loadMoreProducts = () => {
    isSearching.current = true
    setPage(page + 1)
  }

  const clearPageAfterFilter = () => {
    setPage(0)
    window.scroll(0, 0)
  }

  // On component mounted
  useEffect(() => {
    if (initialData) {
      invokeTrack(initialData)
      isSearching.current = false
      setIsFetchData(false)
    }

    updateSort(sortBy)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // check: if have change filter / sort should be reset page number / changeURL for refresh page
  useEffect(() => {
    // if filter reset page as page 0
    setIsFetchData(false)
    clearPageAfterFilter()
    setSortData(sort.by || 'popular')
    onChangeURL()
  }, [filter, sort, onChangeURL])

  useEffect(() => {
    updateSort('popular')
    setchangeSort(false)
    // a new query gets its own size options
    setQuickSize(undefined)
  }, [query, updateSort])

  // check is change the sort filter
  const onSortFilterChanged = () => {
    setchangeSort(true)
    setPage(0)
  }

  // Which response has already been applied, as fetch key + page.
  //
  // This used to key off `prevPage !== page`, which breaks when the sort or a
  // filter changes: React 16 does not batch setState inside an effect, so
  // `setPrevPage(undefined)` commits on its own render while the fetch key is
  // still the old one. The effect then consumed the *previous* response,
  // marked the page as applied, and the correctly sorted response that arrived
  // a moment later was ignored — the request was right and the grid never
  // moved. Keying on the fetch key cannot be fooled by that ordering.
  const appliedResponse = useRef('')

  useEffect(() => {
    if (!results?.products) {
      setIsFetchData(false)
      return
    }

    const stamp = `${fetchOptions}|${page}`

    if (appliedResponse.current !== stamp) {
      appliedResponse.current = stamp
      invokeTrack(results)

      if (page > 0) {
        setProducts((previous) => [...previous, ...results.products])
        // sometime, after load more product, that found totalHits as undefined
        if (results.totalHits) {
          setTotalHits(results.totalHits)
        }
      } else {
        setProducts([...results.products])
        // Freeze the size options for this query.
        //
        // quick_sizes is recomputed for each *filtered* request, so updating it
        // every time made the options disappear as the shopper chose one. Taking
        // the first response's list keeps the query-aware sizes — numeric ones
        // for jeans and shoes, which the static list has none of — while giving
        // the inline rail a list that holds still. Reset when the query changes.
        if (results.quick_sizes) {
          setQuickSize((prev) => (prev?.length ? prev : results.quick_sizes))
        }
        setTotalHits(results.totalHits)
      }

      isSearching.current = false
    }

    setIsFetchData(true)
    // `isFetchData` is in the deps so this re-asserts it: the filter/sort
    // effect clears the flag, and on mount updateSort dispatches and makes that
    // effect run again after this one. Without it the page sits on the loader
    // instead of showing the empty state. Re-running is cheap — the response is
    // already stamped as applied, and setting the same value bails out.
  }, [fetchOptions, invokeTrack, page, results, isFetchData])

  const setShowErrorDefault =
    page === 0 &&
    !products?.length &&
    !changeSort &&
    !checkHasFilterQuickSize(filter)

  if (products?.length === 0 && !isFetchData && !error) {
    return (
      <div className="loader-container">
        <style jsx>{styles}</style>
        <Loader />
      </div>
    )
  }

  // for search and not found product at the page 0
  // that will show full width (ui) of no result
  if (isFetchData && setShowErrorDefault && !error) {
    return <EmptySearchResult query={query} />
  }

  const searchContent = (
    <ValidSearchContent
      filterableCategories={filterableCategories}
      isFetchData={isFetchData}
      isSearching={isSearching}
      loadMoreProducts={loadMoreProducts}
      onSortFilterChanged={onSortFilterChanged}
      products={products}
      query={query}
      quickSizes={quickSizes}
      sortAndFilterData={sortAndFilterData}
      totalHits={totalHits}
    />
  )

  if (error) {
    return (
      <React.Fragment>
        <SearchRequestError retry={retry} isValidating={isValidating} />
        {products.length > 0 && searchContent}
      </React.Fragment>
    )
  }

  return searchContent
}

SearchContent.defaultProps = {
  filterableCategories: null,
  query: '',
  results: null,
  sortBy: 'popular',
}

SearchContent.propTypes = {
  filter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filterableCategories: PropTypes.arrayOf(PropTypes.shape({})),
  query: PropTypes.string,
  results: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({})),
    quick_sizes: PropTypes.arrayOf(PropTypes.string),
    totalHits: PropTypes.number,
  }),
  sort: PropTypes.shape({ by: PropTypes.string }).isRequired,
  sortAndFilterData: PropTypes.shape({}).isRequired,
  track: PropTypes.func.isRequired,
  onChangeURL: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  updateSort: PropTypes.func.isRequired,
}

export default connect(
  (state) => ({
    filter: state.filter,
    sort: state.sort,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        cleanFilters: filterDuck.creators.clean,
        cleanSort: sortDuck.creators.clean,
        updateSort: sortDuck.creators.update,
      },
      dispatch,
    ),
)(SearchContent)
